import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { appPrompt } from '../../../../components/AppConfirmModal'
import {
  archiveUser,
  createUser,
  getUserDetail,
  listAdditionalCostOptions,
  listBranchOptions,
  listCompanyOptions,
  listDepartmentOptions,
  listRoleOptions,
  listShiftOptions,
  listUserOptions,
  resetUserPassword,
  updateUser,
  uploadUserProfilePhoto,
} from '../api/userApi'
import { fetchEmployeeGroupOptions } from '../../../../api/filterOptionApi'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'

function toPhotoUrl(path) {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  if (path.startsWith('/')) return `${API_BASE_URL}${path}`
  return `${API_BASE_URL}/${path}`
}

function normalizeText(value) {
  const text = String(value || '').trim()
  return text.length ? text : undefined
}

function normalizeOptionalNumber(value) {
  if (value === '' || value === null || value === undefined) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

function normalizeOptionalDecimal(value) {
  if (value === '' || value === null || value === undefined) return null
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 0) return null
  return parsed
}

function normalizeAdditionalCostOption(option) {
  const id = Number(option?.id)
  const cost = Number(option?.cost || 0)
  const costPeriod = String(option?.costPeriod || option?.cost_period || 'MONTHLY').toUpperCase()
  const monthlyEquivalentRaw = Number(option?.monthlyEquivalent)
  const monthlyEquivalent = Number.isFinite(monthlyEquivalentRaw)
    ? monthlyEquivalentRaw
    : (costPeriod === 'YEARLY' ? cost / 12 : cost)
  return {
    id,
    reason: option?.reason || option?.additionalReason || option?.name || `Cost #${id}`,
    cost,
    costPeriod,
    monthlyEquivalent,
  }
}

function normalizeOptionsToItems(data) {
  const arr = Array.isArray(data) ? data : []
  return arr.map((option) => ({ value: String(option.id), label: option.name }))
}

export function useUserEntryScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editingUserId = searchParams.get('id')
  const requestedReturnTo = searchParams.get('returnTo')
  const returnTo = requestedReturnTo?.startsWith('/') && !requestedReturnTo.startsWith('//')
    ? requestedReturnTo
    : '/masters/users/list'

  const savingRef = useRef(false)

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    data_scope: 'SELF',
    entity_access: 'SELECTED',
    designation: '',
    joining_date: '',
    shift_master_id: '',
    employee_group_id: '',
    default_dashboard: '',
    erp_employee_id: '',
    role_ids: [],
    company_ids: [],
    branch_ids: [],
    department_ids: [],
    reporting_manager_id: '',
    bruno_access: false,
    productivity_agent_enabled: true,
    CTCperHr: '',
    productive_hours_per_day: '7',
    productive_hours_half_day: '5',
    AdditionalCostCsv: [],
    is_archive: false,
  })

  const [roleOptions, setRoleOptions] = useState([])
  const [companyOptions, setCompanyOptions] = useState([])
  const [branchOptions, setBranchOptions] = useState([])
  const [departmentOptions, setDepartmentOptions] = useState([])
  const [additionalCostOptions, setAdditionalCostOptions] = useState([])
  const [shiftOptions, setShiftOptions] = useState([])
  const [isOrgOptionsLoading, setIsOrgOptionsLoading] = useState(false)

  const [initialLabels, setInitialLabels] = useState({
    company_name: '',
    branch_name: '',
    department_name: '',
    role_name: '',
    reporting_manager_name: '',
    shift_master_name: '',
    employee_group_name: '',
  })

  const [profilePhotoFile, setProfilePhotoFile] = useState(null)
  const [profilePhotoPreview, setProfilePhotoPreview] = useState('')
  const [userCode, setUserCode] = useState('')
  const [snapshot, setSnapshot] = useState(null)

  const [isLoading, setIsLoading] = useState(Boolean(editingUserId))
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [pendingApproval, setPendingApproval] = useState(null)

  // ── Initial Bootstrap: Load Roles, Companies, Shifts, Additional Costs ─────
  useEffect(() => {
    let active = true

    async function loadMasterOptions() {
      try {
        const [roles, companies, additionalCosts, shifts] = await Promise.all([
          listRoleOptions({ page: 1, page_size: 500 }),
          listCompanyOptions({ page: 1, page_size: 500 }),
          listAdditionalCostOptions({ page: 1, page_size: 500 }),
          listShiftOptions({ page: 1, page_size: 200 }),
        ])
        if (!active) return
        setRoleOptions(roles || [])
        setCompanyOptions(companies || [])
        setAdditionalCostOptions(additionalCosts || [])
        setShiftOptions(shifts || [])
      } catch (err) {
        if (!active) return
        console.error('Failed to load initial master options', err)
      }
    }

    async function loadUserIfEditing() {
      if (!editingUserId) return

      const detail = await getUserDetail(editingUserId)
      if (!active) return

      const additionalCsv = Array.isArray(detail?.AdditionalCostCsv) ? detail.AdditionalCostCsv.map(Number) : []

      const roleIds = Array.isArray(detail?.role_ids)
        ? detail.role_ids.map(Number)
        : (detail?.RoleCsv
            ? String(detail.RoleCsv).split(',').map((x) => Number(x.trim())).filter((x) => Number.isFinite(x) && x > 0)
            : [])

      const compIds = Array.isArray(detail?.company_ids)
        ? detail.company_ids.map(Number)
        : (detail?.CompanyCsv
            ? String(detail.CompanyCsv).split(',').map((x) => Number(x.trim())).filter((x) => Number.isFinite(x) && x > 0)
            : [])

      const branchIds = Array.isArray(detail?.branch_ids)
        ? detail.branch_ids.map(Number)
        : (detail?.BranchCsv
            ? String(detail.BranchCsv).split(',').map((x) => Number(x.trim())).filter((x) => Number.isFinite(x) && x > 0)
            : [])

      const deptIds = Array.isArray(detail?.department_ids)
        ? detail.department_ids.map(Number)
        : (detail?.DepartmentCsv
            ? String(detail.DepartmentCsv).split(',').map((x) => Number(x.trim())).filter((x) => Number.isFinite(x) && x > 0)
            : [])

      const nextForm = {
        name: detail?.name || '',
        email: detail?.email || '',
        phone: detail?.phone || '',
        data_scope: detail?.data_scope || 'SELF',
        entity_access: detail?.entity_access || 'SELECTED',
        designation: detail?.designation || '',
        joining_date: detail?.joining_date ? String(detail.joining_date).slice(0, 10) : '',
        shift_master_id: detail?.shift_master_id ? String(detail.shift_master_id) : '',
        employee_group_id: detail?.employee_group_id ? String(detail.employee_group_id) : '',
        default_dashboard: detail?.default_dashboard || '',
        erp_employee_id: detail?.erp_employee_id ? String(detail.erp_employee_id) : '',
        role_ids: roleIds,
        company_ids: compIds,
        branch_ids: branchIds,
        department_ids: deptIds,
        reporting_manager_id: detail?.reporting_manager_id ? String(detail.reporting_manager_id) : '',
        bruno_access: Boolean(detail?.bruno_access),
        productivity_agent_enabled: Boolean(detail?.productivity_agent_enabled),
        CTCperHr: detail?.CTCperHr !== null && detail?.CTCperHr !== undefined ? String(detail.CTCperHr) : '',
        productive_hours_per_day:
          detail?.productive_hours_per_day !== null && detail?.productive_hours_per_day !== undefined
            ? String(detail.productive_hours_per_day)
            : '7',
        productive_hours_half_day:
          detail?.productive_hours_half_day !== null && detail?.productive_hours_half_day !== undefined
            ? String(detail.productive_hours_half_day)
            : '5',
        AdditionalCostCsv: additionalCsv,
        is_archive: Boolean(detail?.is_archive),
      }

      setForm(nextForm)
      setInitialLabels({
        company_name: detail?.company_name || '',
        branch_name: detail?.branch_name || '',
        department_name: detail?.department_name || '',
        role_name: detail?.role_name || '',
        reporting_manager_name: detail?.reporting_manager_name || '',
        shift_master_name: detail?.shift_master_name || '',
        employee_group_name: detail?.employee_group_name || '',
      })
      setUserCode(detail?.user_code || '')
      setProfilePhotoPreview(toPhotoUrl(detail?.profile_photo_path))
      setSnapshot(nextForm)
    }

    async function bootstrap() {
      setIsLoading(true)
      setError('')
      try {
        await Promise.all([loadMasterOptions(), loadUserIfEditing()])
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load user profile.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    bootstrap()

    return () => {
      active = false
    }
  }, [editingUserId])

  // ── Reactive Cascading: Fetch Branches & Departments based on Selection ──
  const companyKey = form.company_ids.slice().sort().join(',')
  const branchKey = form.branch_ids.slice().sort().join(',')

  useEffect(() => {
    let active = true

    if (form.company_ids.length === 0) {
      setBranchOptions([])
      setDepartmentOptions([])
      return
    }

    async function fetchCascadedOptions() {
      setIsOrgOptionsLoading(true)
      try {
        const companyIdsCsv = form.company_ids.join(',')
        const branchIdsCsv = form.branch_ids.length > 0 ? form.branch_ids.join(',') : undefined

        const [branches, departments] = await Promise.all([
          listBranchOptions({ company_ids: companyIdsCsv, page: 1, page_size: 500 }),
          listDepartmentOptions({
            company_ids: companyIdsCsv,
            branch_ids: branchIdsCsv,
            page: 1,
            page_size: 500,
          }),
        ])

        if (!active) return
        setBranchOptions(branches || [])
        setDepartmentOptions(departments || [])
      } catch (err) {
        if (!active) return
        console.error('Failed to load branches and departments for companies', err)
      } finally {
        if (active) setIsOrgOptionsLoading(false)
      }
    }

    fetchCascadedOptions()

    return () => {
      active = false
    }
  }, [companyKey, branchKey])

  // ── Dropdown callbacks for non-cascading masters ──
  const fetchRoleOptions = useCallback(async (search, page, pageSize) => {
    const data = await listRoleOptions({ search, page, page_size: pageSize })
    return { items: normalizeOptionsToItems(data), hasMore: data.length === pageSize }
  }, [])

  const fetchManagerOptions = useCallback(async (search, page, pageSize) => {
    const data = await listUserOptions({ search, page, page_size: pageSize })
    return { items: normalizeOptionsToItems(data), hasMore: data.length === pageSize }
  }, [])

  const fetchShiftOptions = useCallback(async (search, page, pageSize) => {
    const data = await listShiftOptions({ search, page, page_size: pageSize })
    return { items: normalizeOptionsToItems(data), hasMore: data.length === pageSize }
  }, [])

  const completionPercent = useMemo(() => {
    const tracked = [
      form.name,
      form.email,
      form.phone,
      form.role_ids.length ? 'selected' : '',
      form.entity_access === 'ALL' || form.company_ids.length ? 'selected' : '',
      form.reporting_manager_id,
      form.designation,
      form.joining_date,
      form.shift_master_id,
    ]

    const filled = tracked.filter((value) => String(value || '').trim().length > 0).length
    return Math.round((filled / tracked.length) * 100)
  }, [form])

  const selectedAdditionalCostCount = useMemo(
    () => (Array.isArray(form.AdditionalCostCsv) ? form.AdditionalCostCsv.length : 0),
    [form.AdditionalCostCsv],
  )

  const normalizedAdditionalCostOptions = useMemo(
    () =>
      (Array.isArray(additionalCostOptions) ? additionalCostOptions : [])
        .map(normalizeAdditionalCostOption)
        .filter((item) => Number.isFinite(item.id) && item.id > 0),
    [additionalCostOptions],
  )

  const selectedAdditionalCostRows = useMemo(() => {
    const selectedSet = new Set(
      (Array.isArray(form.AdditionalCostCsv) ? form.AdditionalCostCsv : [])
        .map(Number)
        .filter((value) => Number.isFinite(value) && value > 0),
    )
    return normalizedAdditionalCostOptions.filter((item) => selectedSet.has(item.id))
  }, [form.AdditionalCostCsv, normalizedAdditionalCostOptions])

  const selectedAdditionalCostMonthlyTotal = useMemo(
    () => selectedAdditionalCostRows.reduce((sum, row) => sum + Number(row.monthlyEquivalent || 0), 0),
    [selectedAdditionalCostRows],
  )

  function patchForm(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function toggleRole(roleId) {
    const rid = Number(roleId)
    if (!rid) return
    setForm((prev) => {
      const exists = prev.role_ids.includes(rid)
      const nextRoleIds = exists ? prev.role_ids.filter((id) => id !== rid) : [...prev.role_ids, rid]

      const roleObj = roleOptions.find((r) => Number(r.id) === rid)
      const isAdminRole = rid === 1 || String(roleObj?.name || '').trim().toLowerCase() === 'admin'

      let nextDataScope = prev.data_scope
      let nextEntityAccess = prev.entity_access
      let nextIsAdmin = prev.is_admin

      if (!exists && isAdminRole) {
        nextDataScope = 'ALL'
        nextEntityAccess = 'ALL'
        nextIsAdmin = true
      } else if (exists && isAdminRole) {
        const remainingAdmin = nextRoleIds.some((id) => {
          const rObj = roleOptions.find((r) => Number(r.id) === Number(id))
          return Number(id) === 1 || String(rObj?.name || '').trim().toLowerCase() === 'admin'
        })
        if (!remainingAdmin) {
          nextIsAdmin = false
        }
      }

      return {
        ...prev,
        role_ids: nextRoleIds,
        data_scope: nextDataScope,
        entity_access: nextEntityAccess,
        is_admin: nextIsAdmin,
      }
    })
  }

  function selectAllRoles() {
    const allIds = roleOptions.map((r) => Number(r.id)).filter(Boolean)
    setForm((prev) => ({ ...prev, role_ids: allIds }))
  }

  function clearAllRoles() {
    setForm((prev) => ({ ...prev, role_ids: [] }))
  }

  // ── Multi-Select Handlers: Companies with Cascading Auto-Pruning ───────────
  function toggleCompany(companyId) {
    const cid = Number(companyId)
    if (!cid) return

    setForm((prev) => {
      const exists = prev.company_ids.includes(cid)
      const nextCompanyIds = exists
        ? prev.company_ids.filter((id) => id !== cid)
        : [...prev.company_ids, cid]

      const validCompanySet = new Set(nextCompanyIds)

      // Auto-prune branches whose parent company was unselected
      const nextBranchIds = prev.branch_ids.filter((bid) => {
        const branch = branchOptions.find((b) => Number(b.id) === Number(bid))
        return branch ? validCompanySet.has(Number(branch.companyId)) : true
      })

      // Auto-prune departments whose parent company was unselected
      const nextDeptIds = prev.department_ids.filter((did) => {
        const dept = departmentOptions.find((d) => Number(d.id) === Number(did))
        if (!dept) return true
        if (dept.companyId && !validCompanySet.has(Number(dept.companyId))) return false
        return true
      })

      return {
        ...prev,
        company_ids: nextCompanyIds,
        branch_ids: nextBranchIds,
        department_ids: nextDeptIds,
      }
    })
  }

  function selectAllCompanies() {
    const allIds = companyOptions.map((c) => Number(c.id)).filter(Boolean)
    setForm((prev) => ({
      ...prev,
      company_ids: allIds,
    }))
  }

  function clearAllCompanies() {
    setForm((prev) => ({
      ...prev,
      company_ids: [],
      branch_ids: [],
      department_ids: [],
    }))
  }

  // ── Multi-Select Handlers: Branches with Cascading Auto-Pruning ────────────
  function toggleBranch(branchId) {
    const bid = Number(branchId)
    if (!bid) return

    setForm((prev) => {
      const exists = prev.branch_ids.includes(bid)
      const nextBranchIds = exists
        ? prev.branch_ids.filter((id) => id !== bid)
        : [...prev.branch_ids, bid]

      // Auto-prune child departments if their branch is unselected
      let nextDeptIds = prev.department_ids
      if (exists) {
        nextDeptIds = prev.department_ids.filter((did) => {
          const dept = departmentOptions.find((d) => Number(d.id) === Number(did))
          return !dept || Number(dept.branchId) !== bid
        })
      }

      return {
        ...prev,
        branch_ids: nextBranchIds,
        department_ids: nextDeptIds,
      }
    })
  }

  function selectAllBranches() {
    const allBranchIds = branchOptions.map((b) => Number(b.id)).filter(Boolean)
    setForm((prev) => ({
      ...prev,
      branch_ids: allBranchIds,
    }))
  }

  function clearAllBranches() {
    setForm((prev) => ({
      ...prev,
      branch_ids: [],
    }))
  }

  // ── Multi-Select Handlers: Departments ────────────────────────────────────
  function toggleDepartment(departmentId) {
    const did = Number(departmentId)
    if (!did) return

    setForm((prev) => {
      const exists = prev.department_ids.includes(did)
      return {
        ...prev,
        department_ids: exists
          ? prev.department_ids.filter((id) => id !== did)
          : [...prev.department_ids, did],
      }
    })
  }

  function selectAllDepartments() {
    const allDeptIds = departmentOptions.map((d) => Number(d.id)).filter(Boolean)
    setForm((prev) => ({
      ...prev,
      department_ids: allDeptIds,
    }))
  }

  function clearAllDepartments() {
    setForm((prev) => ({
      ...prev,
      department_ids: [],
    }))
  }

  function handlePhotoSelect(file) {
    setProfilePhotoFile(file || null)

    if (!file) return
    const objectUrl = URL.createObjectURL(file)
    setProfilePhotoPreview(objectUrl)
  }

  function toggleAdditionalCost(costId) {
    const id = Number(costId)
    if (!id) return

    setForm((prev) => {
      const current = Array.isArray(prev.AdditionalCostCsv) ? prev.AdditionalCostCsv.map(Number) : []
      const set = new Set(current)
      if (set.has(id)) set.delete(id)
      else set.add(id)
      return { ...prev, AdditionalCostCsv: Array.from(set.values()) }
    })
  }

  async function persistProfilePhoto(targetUserId) {
    if (!profilePhotoFile || !targetUserId) return null
    const result = await uploadUserProfilePhoto(targetUserId, profilePhotoFile)
    return result
  }

  function syncNavbarIfCurrentUser(targetUserId, newPhotoPath) {
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}')
      const currentId = stored?.user_id || stored?.userId || stored?.id
      if (String(currentId) !== String(targetUserId)) return
      if (!newPhotoPath) return
      const updated = { ...stored, profile_photo_path: newPhotoPath }
      localStorage.setItem('user', JSON.stringify(updated))
      window.dispatchEvent(new Event('user-profile-updated'))
    } catch {
      // Non-critical
    }
  }

  async function handleSave() {
    if (savingRef.current) return
    savingRef.current = true
    setError('')
    setSuccess('')

    if (!String(form.name || '').trim()) {
      setError('Full name is required.')
      savingRef.current = false
      return
    }
    if (!String(form.email || '').trim() && !editingUserId) {
      setError('Work email is required.')
      savingRef.current = false
      return
    }
    if (!form.role_ids.length) {
      setError('At least one Role must be selected.')
      savingRef.current = false
      return
    }
    if (form.entity_access === 'SELECTED' && !form.company_ids.length) {
      setError('At least one Company must be selected when Entity Access is set to Selected Entities.')
      savingRef.current = false
      return
    }

    const sharedPayload = {
      name: normalizeText(form.name),
      phone: normalizeText(form.phone),
      data_scope: form.data_scope || 'SELF',
      entity_access: form.entity_access || 'SELECTED',
      is_admin: Boolean(form.is_admin),
      role_ids: form.role_ids.map(Number).filter((id) => Number.isFinite(id) && id > 0),
      company_ids: form.company_ids.map(Number).filter((id) => Number.isFinite(id) && id > 0),
      branch_ids: form.branch_ids.map(Number).filter((id) => Number.isFinite(id) && id > 0),
      department_ids: form.department_ids.map(Number).filter((id) => Number.isFinite(id) && id > 0),
      reporting_manager_id: normalizeOptionalNumber(form.reporting_manager_id),
      designation: normalizeText(form.designation),
      joining_date: normalizeText(form.joining_date),
      shift_master_id: normalizeOptionalNumber(form.shift_master_id),
      employee_group_id: normalizeOptionalNumber(form.employee_group_id),
      default_dashboard: normalizeText(form.default_dashboard),
      erp_employee_id: normalizeOptionalNumber(form.erp_employee_id),
      bruno_access: Boolean(form.bruno_access),
      productivity_agent_enabled: Boolean(form.productivity_agent_enabled),
      CTCperHr: normalizeOptionalDecimal(form.CTCperHr),
      productive_hours_per_day: normalizeOptionalDecimal(form.productive_hours_per_day) ?? 7,
      productive_hours_half_day: normalizeOptionalDecimal(form.productive_hours_half_day) ?? 5,
      AdditionalCostCsv: Array.isArray(form.AdditionalCostCsv)
        ? form.AdditionalCostCsv.map(Number).filter((value) => Number.isFinite(value) && value > 0)
        : [],
    }

    setIsSaving(true)
    try {
      let result
      if (editingUserId) {
        result = await updateUser(editingUserId, {
          ...sharedPayload,
          is_archive: Boolean(form.is_archive),
        })
      } else {
        result = await createUser({
          ...sharedPayload,
          email: normalizeText(form.email)?.toLowerCase(),
        })

        const createdUserId = result?.entity_id
        if (createdUserId) {
          await persistProfilePhoto(createdUserId)
        }
      }
      const message = result?.message || (editingUserId
        ? 'User update submitted for approval.'
        : 'User creation submitted for approval.')
      setPendingApproval({
        requestId: result?.approval_request_id,
        entityId: result?.entity_id,
        message,
      })
      setSuccess(message)
      savingRef.current = false
      setIsSaving(false)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to save user profile.'))
      savingRef.current = false
      setIsSaving(false)
    }
  }

  async function handleArchive() {
    if (!editingUserId) return
    if (savingRef.current) return

    setError('')
    setSuccess('')
    savingRef.current = true
    setIsSaving(true)

    try {
      const result = await archiveUser(editingUserId)
      const message = result?.message || 'User archive submitted for approval.'
      setPendingApproval({
        requestId: result?.approval_request_id,
        entityId: result?.entity_id,
        message,
      })
      setSuccess(message)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to archive user.'))
    } finally {
      savingRef.current = false
      setIsSaving(false)
    }
  }

  async function handleResetPassword() {
    if (!editingUserId) return
    if (savingRef.current) return
    const nextPassword = await appPrompt({ title: 'Reset User Password', message: 'Enter new password for this user:', placeholder: 'Enter new password...', inputType: 'password', confirmText: 'Reset Password' })
    if (!nextPassword || !nextPassword.trim()) return

    setError('')
    setSuccess('')
    savingRef.current = true
    setIsSaving(true)

    try {
      await resetUserPassword(editingUserId, nextPassword.trim())
      setSuccess('Password reset successfully.')
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to reset password.'))
    } finally {
      savingRef.current = false
      setIsSaving(false)
    }
  }

  function handleDiscard() {
    if (!snapshot) {
      navigate(returnTo)
      return
    }

    setForm(snapshot)
    setProfilePhotoFile(null)
    setError('')
    setSuccess('')
  }

  const fetchEmployeeGroupOptionsCallback = useCallback(async () => {
    try {
      const res = await fetchEmployeeGroupOptions({ page: 1, pageSize: 100 })
      const items = res?.items || []
      return items.map((g) => ({
        value: String(g.id),
        label: `${g.name} (${g.weekly_off}${g.second_weekly_off && g.second_weekly_off !== 'None' ? ' / ' + g.second_weekly_off : ''})`,
      }))
    } catch {
      return []
    }
  }, [])

  return {
    state: {
      form: {
        ...form,
      },
      userCode,
      profilePhotoPreview,
      roleOptions,
      companyOptions,
      branchOptions,
      departmentOptions,
      isOrgOptionsLoading,
      additionalCostOptions,
      normalizedAdditionalCostOptions,
      shiftOptions,
      isLoading,
      isSaving,
      error,
      success,
      pendingApproval,
      returnTo,
      isEditMode: Boolean(editingUserId),
      completionPercent,
      selectedAdditionalCostCount,
      selectedAdditionalCostRows,
      selectedAdditionalCostMonthlyTotal,
      initialLabels,
    },
    actions: {
      patchForm,
      toggleRole,
      selectAllRoles,
      clearAllRoles,
      toggleCompany,
      selectAllCompanies,
      clearAllCompanies,
      toggleBranch,
      selectAllBranches,
      clearAllBranches,
      toggleDepartment,
      selectAllDepartments,
      clearAllDepartments,
      handlePhotoSelect,
      toggleAdditionalCost,
      handleSave,
      handleArchive,
      handleResetPassword,
      handleDiscard,
      fetchRoleOptions,
      fetchManagerOptions,
      fetchShiftOptions,
      fetchEmployeeGroupOptions: fetchEmployeeGroupOptionsCallback,
    },
  }
}

